import type { Command } from './types';
import { serializeSubtree, deserializeSubtree, deserializeSubtreeAtIndex } from '@/clipboard/serialize';
import type { ClipboardPayload } from '@/clipboard/types';

function selectedNodeId(ctx: { editorState: { selectedNodeIds: string[] } }): string | undefined {
  return ctx.editorState.selectedNodeIds[0];
}

/**
 * Module-level clipboard storage for when the native Clipboard API
 * is unavailable (e.g., commands triggered from context menu without
 * a ClipboardEvent).
 */
let internalClipboard: ClipboardPayload | null = null;

export function getInternalClipboard(): ClipboardPayload | null {
  return internalClipboard;
}

export function setInternalClipboard(payload: ClipboardPayload | null): void {
  internalClipboard = payload;
}

export const clipboardCommands: Command[] = [
  {
    id: 'clipboard.copy',
    label: 'Copy',
    category: 'clipboard',
    canExecute: (ctx) => !!selectedNodeId(ctx),
    execute: (ctx) => {
      const nodeId = selectedNodeId(ctx);
      if (!nodeId) return;

      const nodes = ctx.doc.getAllNodes();
      const serialized = serializeSubtree(nodeId, nodes);
      if (!serialized) return;

      const payload: ClipboardPayload = {
        type: 'ideatwig-nodes',
        version: 1,
        nodes: [serialized],
      };

      // Store in internal clipboard for use by paste command
      setInternalClipboard(payload);

      // Also try to write to the system clipboard
      void navigator.clipboard.writeText(serialized.text).catch(() => {
        // System clipboard write may fail without user gesture -- that's fine.
      });
    },
  },
  {
    id: 'clipboard.cut',
    label: 'Cut',
    category: 'clipboard',
    canExecute: (ctx) => {
      const nodeId = selectedNodeId(ctx);
      if (!nodeId) return false;
      // Cannot cut the root node
      return nodeId !== ctx.doc.getMeta().rootNodeId;
    },
    execute: (ctx) => {
      const nodeId = selectedNodeId(ctx);
      if (!nodeId) return;

      const nodes = ctx.doc.getAllNodes();
      const serialized = serializeSubtree(nodeId, nodes);
      if (!serialized) return;

      const payload: ClipboardPayload = {
        type: 'ideatwig-nodes',
        version: 1,
        nodes: [serialized],
      };

      setInternalClipboard(payload);

      void navigator.clipboard.writeText(serialized.text).catch(() => {
        // Ignore clipboard API failures
      });

      // Select parent before deleting
      const node = ctx.doc.getNode(nodeId);
      ctx.doc.deleteNode(nodeId);
      if (node?.parentId) {
        ctx.editorState.selectNode(node.parentId);
      } else {
        ctx.editorState.clearSelection();
      }
    },
  },
  {
    id: 'clipboard.paste',
    label: 'Paste',
    category: 'clipboard',
    canExecute: (ctx) => !!selectedNodeId(ctx),
    execute: (ctx) => {
      const parentId = selectedNodeId(ctx);
      if (!parentId) return;

      const payload = getInternalClipboard();
      if (payload && payload.type === 'ideatwig-nodes' && Array.isArray(payload.nodes)) {
        for (const serialized of payload.nodes) {
          const newId = deserializeSubtree(serialized, ctx.doc, parentId);
          ctx.editorState.selectNode(newId);
        }
        return;
      }

      // Fallback: try reading text from system clipboard
      void navigator.clipboard.readText().then((text) => {
        if (text) {
          ctx.doc.addChild(parentId, text);
        }
      }).catch(() => {
        // Clipboard API not available
      });
    },
  },
  {
    id: 'clipboard.duplicate',
    label: 'Duplicate',
    category: 'clipboard',
    canExecute: (ctx) => {
      const nodeId = selectedNodeId(ctx);
      if (!nodeId) return false;
      // Cannot duplicate the root node (it has no parent to insert a sibling into)
      const node = ctx.doc.getNode(nodeId);
      return !!node?.parentId;
    },
    execute: (ctx) => {
      const nodeId = selectedNodeId(ctx);
      if (!nodeId) return;

      const node = ctx.doc.getNode(nodeId);
      if (!node?.parentId) return;

      const parent = ctx.doc.getNode(node.parentId);
      if (!parent) return;

      // Serialize the selected subtree
      const nodes = ctx.doc.getAllNodes();
      const serialized = serializeSubtree(nodeId, nodes);
      if (!serialized) return;

      // Find the current node's position in the parent's childIds
      const currentIndex = parent.childIds.indexOf(nodeId);
      const insertIndex = currentIndex >= 0 ? currentIndex + 1 : parent.childIds.length;

      // Deserialize as a sibling right after the current node
      const newId = deserializeSubtreeAtIndex(serialized, ctx.doc, node.parentId, insertIndex);
      ctx.editorState.selectNode(newId);
    },
  },
];
